import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './App.css';
import carImage from './assets/car.avif';
import creditCardImage from './assets/creditcard.avif';
import mobileAppImage from './assets/mobileapp.avif';
import logo from './assets/logo.png';

function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [monthlyPayment, setMonthlyPayment] = useState(650);

  const REWARDS_RATE_FLOOR = 0.19;
  const REWARDS_RATE_CEILING = 0.48;
  const REWARDS_CAP = 200;
  const POINTS_PER_DOLLAR = 100;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGetStarted = () => {
    window.location.href = 'https://app.lucidblack.io';
  };

  const handleLogin = () => {
    window.location.href = 'https://app.lucidblack.io';
  };

  const handleMonthlyPaymentChange = (event) => {
    const nextValue = Number(event.target.value);
    if (Number.isNaN(nextValue)) {
      return;
    }
    setMonthlyPayment(Math.max(0, Math.min(2000, nextValue)));
  };

  const estimatedRewards = (() => {
    if (monthlyPayment > 1000) {
      return { low: REWARDS_CAP, high: REWARDS_CAP };
    }

    const low = Math.round(Math.min(monthlyPayment * REWARDS_RATE_FLOOR, REWARDS_CAP));
    const high = Math.round(Math.min(monthlyPayment * REWARDS_RATE_CEILING, REWARDS_CAP));
    return { low, high };
  })();

  const formatMoneyRange = (low, high) => {
    if (low === high) {
      return `$${low.toLocaleString()}/mo`;
    }
    return `$${low.toLocaleString()}-$${high.toLocaleString()}/mo`;
  };

  const formatPointsRange = (low, high) => {
    const lowPoints = low * POINTS_PER_DOLLAR;
    const highPoints = high * POINTS_PER_DOLLAR;
    if (lowPoints === highPoints) {
      return `${lowPoints.toLocaleString()} pts/mo`;
    }
    return `${lowPoints.toLocaleString()}-${highPoints.toLocaleString()} pts/mo`;
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className={`nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="nav-content">
            <div className="logo">
              <Link to="/">
                <img src={logo} alt="LucidBlack" className="logo-image" />
              </Link>
            </div>
            <div className="nav-links">
              <a href="#features">Features</a>
              <a href="#how-it-works">How It Works</a>
              <a href="#benefits">Benefits</a>
              <button className="btn-text" onClick={handleLogin}>
                Login
              </button>
              <button className="btn-secondary" onClick={handleGetStarted}>
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-gradient"></div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                Turn Car Payments into
                <span className="gradient-text"> Travel & Cash</span>
              </h1>
              <p className="hero-subtitle">
                The first credit card that rewards you with points for every monthly car loan payment.
                Turn routine payments into travel, cash, or vehicle maintenance.
              </p>
              <div className="hero-cta">
                <button onClick={handleGetStarted} className="btn-primary">
                  Start Earning Rewards
                </button>
                <button onClick={handleLogin} className="btn-outline">
                  Login to Account
                </button>
              </div>
              <p className="hero-note">
                Quick approval process. Start earning rewards on your next payment.
              </p>
            </div>
            <div className="hero-image">
              <div className="image-wrapper hero-car-wrapper">
                <img src={carImage} alt="Luxury Car" className="hero-car" />
              </div>
              <div className="rewards-estimator" aria-label="Estimate your rewards">
                <div className="estimator-header">
                  <h3>Estimate your rewards</h3>
                </div>
                <div className="estimator-controls">
                  <label htmlFor="monthly-payment" className="estimator-label">
                    Monthly payment
                  </label>
                  <div className="estimator-input-wrap">
                    <span>$</span>
                    <input
                      id="monthly-payment"
                      type="number"
                      min="0"
                      max="2000"
                      step="25"
                      value={monthlyPayment}
                      onChange={handleMonthlyPaymentChange}
                    />
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="2000"
                    step="25"
                    value={monthlyPayment}
                    onChange={handleMonthlyPaymentChange}
                    className="estimator-slider"
                  />
                </div>
                <div className="estimator-results">
                  <div className="estimator-result">
                    <span>Estimated cash</span>
                    <strong>{formatMoneyRange(estimatedRewards.low, estimatedRewards.high)}</strong>
                  </div>
                  <div className="estimator-result">
                    <span>Estimated points</span>
                    <strong>{formatPointsRange(estimatedRewards.low, estimatedRewards.high)}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-it-works">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">Three simple steps to start earning rewards</p>
          </div>
          <div className="steps">
            <div className="step">
              <div className="step-number">01</div>
              <h3>Quick Approval</h3>
              <p>Apply for your LucidBlack credit card in minutes with our streamlined approval process.</p>
            </div>
            <div className="step-connector"></div>
            <div className="step">
              <div className="step-number">02</div>
              <h3>Link Your Loan</h3>
              <p>Easily connect your existing car loan through our secure platform.</p>
            </div>
            <div className="step-connector"></div>
            <div className="step">
              <div className="step-number">03</div>
              <h3>Earn Rewards</h3>
              <p>Make on-time payments and watch your reward points accumulate automatically.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose LucidBlack?</h2>
            <p className="section-subtitle">
              Transparent financing with no hidden fees. We're your financial ally in auto financing.
            </p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Rewards on Every Payment</h3>
              <p>
                Earn reward points on each monthly car loan payment. Redeem for travel, cash back, or vehicle maintenance services.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 12V22H4V12M2 7L12 2L22 7V10H2V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 22V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Zero Processing Fees</h3>
              <p>
                No hidden costs or processing fees on your car loan payments. What you see is what you get.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Payment Flexibility</h3>
              <p>
                Skip up to 3 payments per year. Life happens, and we've got you covered with real flexibility.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Showcase */}
      <section className="showcase">
        <div className="container">
          <div className="showcase-grid">
            <div className="showcase-item">
              <img src={mobileAppImage} alt="Mobile App Interface" className="showcase-image showcase-mobile" />
              <div className="showcase-content">
                <h3>Seamless Mobile Experience</h3>
                <p>Manage your payments, track rewards, and control your account from anywhere.</p>
              </div>
            </div>
            <div className="showcase-item reverse">
              <div className="showcase-content">
                <h3>Premium Card Design</h3>
                <p>A sleek, modern card that makes a statement while earning you rewards.</p>
              </div>
              <div className="card-glow-wrapper">
                <img src={creditCardImage} alt="Credit Card Design" className="showcase-image showcase-card" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section with Car Image */}
      <section id="benefits" className="benefits">
        <div className="container">
          <div className="benefits-content">
            <div className="benefits-image">
              <img src={carImage} alt="Luxury Car" className="car-image" />
            </div>
            <div className="benefits-text">
              <h2>Your Car Payment, <br />But Smarter</h2>
              <div className="benefits-list">
                <div className="benefit-item">
                  <div className="benefit-check">✓</div>
                  <div>
                    <h4>Transform Every Payment</h4>
                    <p>Turn your monthly obligation into earning opportunities</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-check">✓</div>
                  <div>
                    <h4>No Hidden Surprises</h4>
                    <p>Complete transparency with zero hidden fees or charges</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-check">✓</div>
                  <div>
                    <h4>Available Now</h4>
                    <p>Join thousands already experiencing revolutionary auto financing</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Transform Your Car Payments?</h2>
            <p>Join thousands already earning rewards on their monthly car loan payments</p>
            <div className="cta-buttons">
              <button onClick={handleGetStarted} className="btn-primary-large">
                Start Earning Rewards
              </button>
              <button onClick={handleLogin} className="btn-outline-large">
                Login to Existing Account
              </button>
            </div>
            <p className="cta-note">Quick approval • No hidden fees • Start earning rewards immediately</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src={logo} alt="LucidBlack" className="footer-logo" />
              <p>Revolutionizing auto financing, one payment at a time.</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <a href="#features">Features</a>
                <a href="#how-it-works">How It Works</a>
                <a href="#benefits">Benefits</a>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <a href="#about">About</a>
                <a href="#contact">Contact</a>
                <a href="#careers">Careers</a>
              </div>
              <div className="footer-column">
                <h4>Legal</h4>
                <Link to="/privacy">Privacy</Link>
                <Link to="/terms">Terms</Link>
                <a href="#compliance">Compliance</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 LucidBlack. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
